import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Smartphone, RefreshCw, ShieldCheck, ShieldAlert, AlertTriangle, Copy, Check, X,
} from 'lucide-react';
import {
  instances as instancesApi,
  type BryveTfaKonto, type BryveTfaEinrichtung, type BryveErgebnis,
} from '../api';

/**
 * Der zweite Faktor der Admin-Konten einer bryve-Instanz.
 *
 * ══ WOGEGEN DAS GEBAUT IST ═════════════════════════════════════════════════
 *
 * bryve verweigert einem SUPERADMIN ohne zweiten Faktor nach einer Frist die
 * Anmeldung und verweist dabei auf „einen anderen SUPERADMIN". Gibt es den
 * nicht — und es gibt ihn selten —, ist das eine Sackgasse: der Reset in der
 * Anwendung (`users/:id/2fa/reset`) verlangt selbst SUPERADMIN.
 *
 * Am 29.09.2026 stand `juni-sales-prod-bryve` seit neunzehn Tagen so da, ohne
 * dass es jemandem aufgefallen war. Man sieht es sonst erst, wenn man
 * davorsteht.
 *
 * Deshalb zeigt diese Ansicht nicht nur, wer einen zweiten Faktor hat, sondern
 * auch, wen die Frist als naechstes trifft.
 *
 * ══ MEHRERE GERAETE ════════════════════════════════════════════════════════
 *
 * Braucht keine Aenderung am Produkt. Ein TOTP-Geheimnis ist eine
 * Zeichenkette; jede App, die denselben QR-Code liest, erzeugt dieselben
 * Ziffern. Wer zwei Telefone einrichten will, scannt zweimal — solange der
 * Code auf dem Bildschirm steht. Deshalb bleibt er hier stehen, bis man ihn
 * ausdruecklich wegklickt.
 */

function Zeile({
  k, onZuruecksetzen, onEinrichten, laeuft,
}: {
  k: BryveTfaKonto;
  onZuruecksetzen: () => void;
  onEinrichten: () => void;
  laeuft: boolean;
}) {
  return (
    <li className={`px-4 py-2.5 ${k.ausgesperrt ? 'bg-rose-50' : ''}`}>
      <div className="flex flex-wrap items-start gap-2">
        {k.an
          ? <ShieldCheck size={14} className="mt-0.5 shrink-0 text-emerald-600" />
          : <ShieldAlert size={14} className={`mt-0.5 shrink-0 ${k.ausgesperrt ? 'text-rose-600' : 'text-slate-400'}`} />}

        <span className="min-w-0 flex-1">
          <span className="text-xs font-medium text-slate-800">{k.email}</span>
          <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
            {k.rolle}
          </span>
          {/* Ein Konto mit API-Schlüssel arbeitet Server zu Server. Die
              2FA-Pflicht sitzt in /api/auth/login und trifft es nie — ohne
              diesen Hinweis sähe es aus wie ein vergessenes Admin-Konto. */}
          {k.apiSchluessel > 0 && (
            <span
              title={`${k.apiSchluessel} API-Schlüssel — meldet sich nicht an, die Frist gilt nicht`}
              className="ml-1 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-800"
            >
              Dienstkonto
            </span>
          )}
          <span className="block text-[10px] leading-relaxed text-slate-500">
            {k.an ? (
              <>
                eingerichtet {k.eingerichtetAm ?? '?'} · {k.codes} von 10 Notfallcodes übrig
                {k.vertrauteGeraete > 0 && ` · ${k.vertrauteGeraete} vertraute Browser`}
                {!k.hatSecret && ' · ACHTUNG: kein Geheimnis hinterlegt'}
              </>
            ) : k.apiSchluessel > 0 ? (
              <>
                kein zweiter Faktor, aber auch keine Anmeldung — arbeitet über
                {' '}{k.apiSchluessel === 1 ? 'einen API-Schlüssel' : `${k.apiSchluessel} API-Schlüssel`}
              </>
            ) : (
              <>
                kein zweiter Faktor · Konto seit {k.angelegtAm}
                {k.rolle === 'SUPERADMIN' && !k.ausgesperrt && (
                  <> · Frist dieser Instanz: {k.fristTage} Tage</>
                )}
              </>
            )}
          </span>
          {k.ausgesperrt && (
            <span className="mt-1 flex items-start gap-1 text-[11px] leading-relaxed text-rose-800">
              <AlertTriangle size={11} className="mt-px shrink-0" />
              Dieses Konto kommt nicht mehr hinein: SUPERADMIN ohne zweiten Faktor, älter als
              die Frist dieser Instanz ({k.fristTage} Tage). Hier einrichten — ein Reset macht
              es nur schlimmer.
            </span>
          )}
        </span>

        <span className="flex shrink-0 gap-1.5">
          {k.an && (
            <button
              type="button"
              onClick={onZuruecksetzen}
              disabled={laeuft}
              className="rounded border border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              abschalten
            </button>
          )}
          <button
            type="button"
            onClick={onEinrichten}
            disabled={laeuft}
            className="rounded bg-blue-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {k.an ? 'neu einrichten' : 'einrichten'}
          </button>
        </span>
      </div>
    </li>
  );
}

/** Der QR-Code samt Notfallcodes. Bleibt stehen, bis jemand ihn wegklickt. */
function Einrichtung({ e, onSchliessen }: { e: BryveTfaEinrichtung; onSchliessen: () => void }) {
  const [kopiert, setKopiert] = useState<'secret' | 'codes' | null>(null);

  const kopiere = (was: 'secret' | 'codes', text: string) => {
    navigator.clipboard.writeText(text);
    setKopiert(was);
    window.setTimeout(() => setKopiert(null), 2000);
  };

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="text-sm font-semibold text-emerald-900">
            Zweiter Faktor für {e.email} eingerichtet
          </h4>
          <p className="mt-0.5 text-[11px] leading-relaxed text-emerald-800">
            Jetzt mit <strong>jedem Gerät scannen</strong>, das ihn tragen soll — zwei Telefone,
            ein Tablet, was auch immer. Nach dem Schliessen ist der Code weg.
          </p>
        </div>
        <button
          type="button"
          onClick={onSchliessen}
          className="shrink-0 rounded border border-emerald-300 px-2 py-1 text-[10px] text-emerald-800 hover:bg-emerald-100"
        >
          <X size={11} />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-4">
        {e.qr && (
          <img
            src={e.qr}
            alt={`QR-Code für ${e.email}`}
            className="h-44 w-44 shrink-0 rounded-lg border border-emerald-200 bg-white p-1.5"
          />
        )}

        <div className="min-w-[15rem] flex-1 space-y-2.5">
          {e.secret && (
            <div>
              <span className="text-[10px] font-medium text-emerald-900">
                Für Apps ohne Kamera
              </span>
              <div className="mt-0.5 flex gap-1">
                <code className="min-w-0 flex-1 break-all rounded bg-white px-2 py-1 font-mono text-[10px] text-slate-800">
                  {e.secret}
                </code>
                <button
                  type="button"
                  onClick={() => kopiere('secret', e.secret!)}
                  className="shrink-0 rounded border border-emerald-300 px-1.5 text-emerald-800 hover:bg-emerald-100"
                >
                  {kopiert === 'secret' ? <Check size={11} /> : <Copy size={11} />}
                </button>
              </div>
            </div>
          )}

          {e.codes.length > 0 && (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-emerald-900">
                  {e.codes.length} Notfallcodes — je einmal verwendbar
                </span>
                <button
                  type="button"
                  onClick={() => kopiere('codes', e.codes.join('\n'))}
                  className="inline-flex items-center gap-1 rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] text-emerald-800 hover:bg-emerald-100"
                >
                  {kopiert === 'codes' ? <Check size={10} /> : <Copy size={10} />} alle
                </button>
              </div>
              <div className="mt-0.5 grid grid-cols-2 gap-x-3 gap-y-0.5 rounded bg-white px-2 py-1.5">
                {e.codes.map((c) => (
                  <code key={c} className="font-mono text-[10px] text-slate-800">{c}</code>
                ))}
              </div>
              <p className="mt-1 text-[10px] leading-relaxed text-emerald-800">
                In den Passwortspeicher, nicht in eine Mail. Sie sind der einzige Weg zurück,
                wenn das Gerät verloren geht.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BryveTfaPanel({ instanceId }: { instanceId: string }) {
  const qc = useQueryClient();
  const [einrichtung, setEinrichtung] = useState<BryveTfaEinrichtung | null>(null);
  const [meldung, setMeldung] = useState<BryveErgebnis | null>(null);

  const stand = useQuery({
    queryKey: ['bryve-tfa', instanceId],
    queryFn: () => instancesApi.bryveTfa(instanceId),
    retry: false,
  });

  const zuruecksetzen = useMutation({
    mutationFn: (email: string) => instancesApi.bryveTfaZuruecksetzen(instanceId, email),
    onSuccess: (e) => {
      setMeldung(e);
      qc.invalidateQueries({ queryKey: ['bryve-tfa', instanceId] });
    },
    onError: (e: Error) => setMeldung({ ok: false, meldung: e.message, wege: [] }),
  });

  const einrichten = useMutation({
    mutationFn: (email: string) => instancesApi.bryveTfaEinrichten(instanceId, email),
    onSuccess: (e) => {
      setMeldung(null);
      if (e.ok) setEinrichtung(e);
      else setMeldung({ ok: false, meldung: e.meldung, wege: [] });
      qc.invalidateQueries({ queryKey: ['bryve-tfa', instanceId] });
    },
    onError: (e: Error) => setMeldung({ ok: false, meldung: e.message, wege: [] }),
  });

  const laeuft = zuruecksetzen.isPending || einrichten.isPending;
  const konten = stand.data?.konten ?? [];
  const gesperrt = konten.filter((k) => k.ausgesperrt);

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <Smartphone size={14} /> Zwei-Faktor
            </h3>
            <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
              Ein SUPERADMIN ohne zweiten Faktor wird nach Ablauf der Frist ausgesperrt — und
              der Reset in der Anwendung verlangt selbst einen SUPERADMIN.
            </p>
          </div>
          <button
            type="button"
            onClick={() => stand.refetch()}
            disabled={stand.isFetching}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw size={12} className={stand.isFetching ? 'animate-spin' : ''} />
          </button>
        </div>

        {gesperrt.length > 0 && (
          <p className="flex items-start gap-1.5 bg-rose-50 px-4 py-2 text-xs leading-relaxed text-rose-900">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            {gesperrt.length === 1
              ? '1 Konto kommt nicht mehr hinein.'
              : `${gesperrt.length} Konten kommen nicht mehr hinein.`}{' '}
            Hier einen zweiten Faktor einrichten stellt den Zugang wieder her.
          </p>
        )}

        {stand.isError ? (
          <p className="px-4 py-3 text-[11px] text-rose-700">{(stand.error as Error).message}</p>
        ) : stand.isLoading ? (
          <p className="px-4 py-3 text-[11px] text-slate-400">wird aus der Instanz gelesen …</p>
        ) : konten.length === 0 ? (
          <p className="px-4 py-3 text-[11px] text-slate-500">Keine Admin-Konten gefunden.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {konten.map((k) => (
              <Zeile
                key={k.email}
                k={k}
                laeuft={laeuft}
                onZuruecksetzen={() => zuruecksetzen.mutate(k.email)}
                onEinrichten={() => einrichten.mutate(k.email)}
              />
            ))}
          </ul>
        )}

        {laeuft && (
          <p className="border-t border-slate-100 px-4 py-1.5 text-[10px] text-slate-400">
            läuft über den Wirt, das dauert einen Moment …
          </p>
        )}
      </div>

      {einrichtung && (
        <Einrichtung e={einrichtung} onSchliessen={() => setEinrichtung(null)} />
      )}

      {meldung && (
        <div
          className={`rounded-lg border px-3 py-2 ${
            meldung.ok
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          <div className="flex items-start gap-1.5">
            {meldung.ok ? <Check size={13} className="mt-0.5 shrink-0" />
              : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
            <p className="min-w-0 flex-1 text-xs leading-relaxed">{meldung.meldung}</p>
            <button
              type="button"
              onClick={() => setMeldung(null)}
              className="shrink-0 text-xs opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
